import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ENVIRONMENT } from '../../environments/environment';
import {
  AbnLookupRequest,
  AbnLookupResponse,
  ApiBusinessTypeResponse,
  ApiMealTypeResponse,
  ApiResponse,
  BusinessProfileResponse,
  BusinessSocialMediaLinks,
  BusinessTypeResponse,
  CheckEmailRequest,
  CheckEmailResponse,
  CheckMobileRequest,
  CheckMobileResponse,
  CollabGoal,
  SendMobileOtpRequest,
  SendMobileOtpResponse,
  SendOtpRequest,
  SendOtpResponse,
  SettingResponse,
  SuburbSuggestionModel,
  TermsResponse,
  UploadImageRequest,
  UploadImageResponse,
  UploadImageResponses,
  VerifyMobileOtpRequest,
  VerifyMobileOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '../features/auth/forms/forms.model';
import { SuburbSuggestion } from './signup.service';

@Injectable({
  providedIn: 'root',
})
export class UserRegistrationService {
  private http = inject(HttpClient);
  private apiUrl: string = ENVIRONMENT.baseUrls.baseAPIUrl;

  constructor() { }

  getTermsAndConditions(): Observable<ApiResponse<{ Title: string, TermsAndConditions: string }>> {
    return this.http.get<ApiResponse<{ Title: string, TermsAndConditions: string }>>(`${this.apiUrl}Business/get_terms_conditions`);
  }
  getBusinessTypes(): Observable<ApiBusinessTypeResponse> {
    return this.http.get<ApiBusinessTypeResponse>(
      `${this.apiUrl}Business/get_business_types`
    );
  }
  uploadImage(imageData: UploadImageRequest[]): Observable<any> {
    return this.http.post<UploadImageResponses>(
      `${this.apiUrl}Business/upload_image`,
      imageData
    );
  }
  uploadLogo(imageData: UploadImageRequest): Observable<UploadImageResponse> {
    return this.http.post<UploadImageResponse>(
      `${this.apiUrl}Business/upload_profile_logo`,
      imageData
    );
  }
  insertBusiness(businessData: any): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}Business/insert_business`,
      businessData
    );
  }
  getCollabGoals(): Observable<ApiResponse<CollabGoal[]>> {
    return this.http.get<ApiResponse<CollabGoal[]>>(
      `${this.apiUrl}Business/get_collab_goals`
    );
  }
  getSettingOptions(): Observable<ApiResponse<SettingResponse[]>> {
    return this.http.get<ApiResponse<SettingResponse[]>>(
      `${this.apiUrl}Business/get_setting_options`
    );
  }
  getMealTypes(): Observable<ApiMealTypeResponse> {
    return this.http.get<ApiMealTypeResponse>(
      `${this.apiUrl}Business/get_meal_types`
    );
  }

  sendEmailOtp(
    request: SendOtpRequest
  ): Observable<ApiResponse<SendOtpResponse>> {
    return this.http.post<ApiResponse<SendOtpResponse>>(
      `${this.apiUrl}Auth/send_email_otp`,
      request
    );
  }
  sendEmailOtpChangePassword(
    request: SendOtpRequest
  ): Observable<ApiResponse<SendOtpResponse>> {
    return this.http.post<ApiResponse<SendOtpResponse>>(
      `${this.apiUrl}Auth/send_email_otp_change_passord`,
      request
    );
  }

  verifyEmailOtp(
    request: VerifyOtpRequest
  ): Observable<ApiResponse<VerifyOtpResponse>> {
    return this.http.post<ApiResponse<VerifyOtpResponse>>(
      `${this.apiUrl}Auth/verify_email_otp`,
      request
    );
  }

  lookupABN(abn: string): Observable<ApiResponse<AbnLookupResponse>> {
    const sanitizedABN = abn.replace(/\s/g, '');
    return this.http.post<ApiResponse<AbnLookupResponse>>(
      `${this.apiUrl}Auth/lookup_abn?abn=${encodeURIComponent(sanitizedABN)}`,
      {}
    );
  }

  getBusinessProfile(
    businessId: number
  ): Observable<ApiResponse<BusinessProfileResponse>> {
    return this.http.get<ApiResponse<BusinessProfileResponse>>(
      `${this.apiUrl}Business/business-profile/${businessId}`
    );
  }

  updateBusinessProfile(payload: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}Business/update_business_profile`,
      payload
    );
  }

  sendMobileOtp(
    request: SendMobileOtpRequest
  ): Observable<ApiResponse<SendMobileOtpResponse>> {
    return this.http.post<ApiResponse<SendMobileOtpResponse>>(
      `${this.apiUrl}Auth/send-otp`,
      request
    );
  }

  sendMobileOtpForUpdateBusinessProfile(
    request: SendMobileOtpRequest
  ): Observable<ApiResponse<SendMobileOtpResponse>> {
    return this.http.post<ApiResponse<SendMobileOtpResponse>>(
      `${this.apiUrl}Auth/mobile_otp_update_business_profile`,
      request
    );
  }


  verifyMobileOtp(
    request: VerifyMobileOtpRequest
  ): Observable<ApiResponse<VerifyMobileOtpResponse>> {
    return this.http.post<ApiResponse<VerifyMobileOtpResponse>>(
      `${this.apiUrl}Auth/verify-otp`,
      request
    );
  }
  getBusinessSocialMediaLink(
    businessId: number
  ): Observable<ApiResponse<BusinessSocialMediaLinks>> {
    return this.http.get<ApiResponse<BusinessSocialMediaLinks>>(
      `${this.apiUrl}Business/business_social_media_link/${businessId}`
    );
  }

  deleteUser(payload: { Email: string }): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}Settings/delete_user`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_access_token')}`
        }
      }
    );
  }
  checkMobileNumber(
    request: CheckMobileRequest
  ): Observable<ApiResponse<CheckMobileResponse>> {
    return this.http.post<ApiResponse<CheckMobileResponse>>(
      `${this.apiUrl}Auth/check-mobile`,
      request
    );
  }

  checkEmail(
    request: CheckEmailRequest
  ): Observable<ApiResponse<CheckEmailResponse>> {
    return this.http.post<ApiResponse<CheckEmailResponse>>(
      `${this.apiUrl}Auth/check-email`,
      request
    );
  }
  getSuburbSuggestions(searchTerm: string): Observable<ApiResponse<SuburbSuggestion[]>> {
    const url = `${this.apiUrl}business/get_suburb_regions?searchTerm=${encodeURIComponent(searchTerm)}`;
    return this.http.get<ApiResponse<SuburbSuggestion[]>>(url);
  }
 getTourStatus(userId: number): Observable<ApiResponse<{ isTourCompleted: boolean }>> {
  return this.http.post<ApiResponse<{ isTourCompleted: boolean }>>(
    `${this.apiUrl}Auth/get_tour_status`,
    { UserId: userId }
  );
}

updateTourStatus(userId: number): Observable<ApiResponse<any>> {
  return this.http.post<ApiResponse<any>>(
    `${this.apiUrl}Auth/update_tour_status`,
    { UserId: userId }
  );
}
updateBusinessCollabGoals(payload: any): Observable<ApiResponse<any>> {
  return this.http.post<ApiResponse<any>>(
    `${this.apiUrl}Business/update_business_collab_goals`, 
    payload
  );
}

getMyProfileInfo(userId: number): Observable<ApiResponse<any>> {
  return this.http.get<ApiResponse<any>>(
    `${this.apiUrl}Business/my-profile-info/${userId}`
  );
}

updateBusinessContactInfo(payload: any): Observable<ApiResponse<any>> {
  return this.http.post<ApiResponse<any>>(
    `${this.apiUrl}Business/update_business_contact_info`,
    payload
  );
}
}
